import React, { useState } from 'react';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, Package, Truck, ShoppingCart, User, FileText, CheckCircle, XCircle, ArrowRight } from 'lucide-react';

const SerialNumberTracker = () => {
  const [serialInput, setSerialInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    const sn = serialInput.trim();
    if (!sn) {
      toast.error('Please enter a serial number');
      return;
    }
    setLoading(true);
    setResult(null);
    setNotFound(false);
    try {
      const response = await api.get(`/serial-tracker/search/${encodeURIComponent(sn)}`);
      setResult(response.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Failed to search serial number');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isSold = result?.current_status === 'Sold Out';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900" data-testid="serial-tracker-title">Stock Track by Serial Number</h1>
        <p className="text-gray-600">Enter a product serial number to view its complete lifecycle</p>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-3 items-end">
            <div className="flex-1">
              <Input
                value={serialInput}
                onChange={(e) => setSerialInput(e.target.value.toUpperCase())}
                placeholder="Enter Serial Number (e.g. SN-001)"
                className="text-lg h-12 font-mono"
                data-testid="serial-search-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="h-12 px-8" data-testid="serial-search-btn">
              <Search className="h-4 w-4 mr-2" />
              {loading ? 'Searching...' : 'Track'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Not Found */}
      {notFound && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="pt-6 text-center">
            <XCircle className="h-12 w-12 text-orange-400 mx-auto mb-3" />
            <p className="text-lg font-semibold text-orange-800">Serial Number Not Found</p>
            <p className="text-orange-600 mt-1">No product with serial number "{serialInput}" was found in the system.</p>
          </CardContent>
        </Card>
      )}

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {/* Header */}
          <Card className={`border-2 ${isSold ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-xl ${isSold ? 'bg-red-200' : 'bg-green-200'}`}>
                    <Package className={`h-8 w-8 ${isSold ? 'text-red-700' : 'text-green-700'}`} />
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 font-medium">Serial Number</p>
                    <p className="text-2xl font-bold font-mono" data-testid="serial-result-number">{result.serial_number}</p>
                  </div>
                </div>
                <Badge
                  className={`text-base px-4 py-2 ${isSold ? 'bg-red-600 hover:bg-red-600 text-white' : 'bg-green-600 hover:bg-green-600 text-white'}`}
                  data-testid="serial-status-badge"
                >
                  {isSold ? 'SOLD OUT' : 'IN STOCK'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Timeline */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Product Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-4 w-4 text-blue-600" />
                  Product Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <InfoRow label="Product" value={result.product?.name || '-'} bold />
                <InfoRow label="Model" value={result.product?.model || '-'} />
                <InfoRow label="SKU" value={result.product?.sku || '-'} mono />
                <InfoRow label="Brand" value={result.product?.brand || '-'} />
                <InfoRow label="Category" value={result.product?.category || '-'} />
                <InfoRow label="HSN" value={result.product?.hsn_number || '-'} mono />
              </CardContent>
            </Card>

            {/* Purchase Info */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 text-amber-600" />
                  Purchase Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {result.purchase?.purchase_number ? (
                  <>
                    <InfoRow label="Purchase #" value={result.purchase.purchase_number} mono bold />
                    <InfoRow label="Purchase Date" value={formatDate(result.purchase.purchase_date)} />
                    <InfoRow label="Invoice #" value={result.purchase.invoice_number || '-'} mono />
                    <InfoRow label="Purchase Price" value={`₹${(result.purchase.purchase_price || 0).toLocaleString('en-IN')}`} bold />
                    <hr className="my-2" />
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-800">{result.purchase.supplier_name}</span>
                    </div>
                    {result.purchase.supplier_contact && (
                      <p className="text-xs text-gray-500 ml-6">{result.purchase.supplier_contact}</p>
                    )}
                    {result.purchase.supplier_gst && (
                      <p className="text-xs text-gray-500 ml-6 font-mono">GST: {result.purchase.supplier_gst}</p>
                    )}
                  </>
                ) : (
                  <p className="text-gray-400 text-sm">No purchase data available</p>
                )}
              </CardContent>
            </Card>

            {/* Sale Info */}
            <Card className={isSold ? 'ring-2 ring-red-200' : ''}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className={`h-4 w-4 ${isSold ? 'text-red-600' : 'text-gray-400'}`} />
                  {isSold ? 'Sale Details' : 'Sale Status'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {isSold && result.sale?.bill_number ? (
                  <>
                    <InfoRow label="Invoice #" value={result.sale.bill_number} mono bold />
                    <InfoRow label="Bill Date" value={formatDate(result.sale.bill_date)} />
                    <InfoRow label="Bill Type" value={result.sale.bill_type || '-'} />
                    <InfoRow label="Net Amount" value={`₹${(result.sale.net_amount || 0).toLocaleString('en-IN')}`} bold />
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Payment:</span>
                      <Badge variant="outline" className={
                        result.sale.payment_status === 'Paid' ? 'bg-green-50 text-green-700' :
                        result.sale.payment_status === 'Partial' ? 'bg-amber-50 text-amber-700' :
                        'bg-red-50 text-red-700'
                      }>
                        {result.sale.payment_status || '-'}
                      </Badge>
                    </div>
                    {result.sale.amount_paid > 0 && (
                      <InfoRow label="Amount Paid" value={`₹${result.sale.amount_paid.toLocaleString('en-IN')}`} />
                    )}
                    <hr className="my-2" />
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="font-medium text-gray-800">{result.sale.customer_name || '-'}</span>
                    </div>
                    {result.sale.customer_contact && (
                      <p className="text-xs text-gray-500 ml-6">{result.sale.customer_contact}</p>
                    )}
                    {result.sale.customer_address && (
                      <p className="text-xs text-gray-500 ml-6">{result.sale.customer_address}</p>
                    )}
                    {result.sale.customer_gst && (
                      <p className="text-xs text-gray-500 ml-6 font-mono">GST: {result.sale.customer_gst}</p>
                    )}
                  </>
                ) : isSold ? (
                  <p className="text-gray-400 text-sm">Sold but sale details not linked</p>
                ) : (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <CheckCircle className="h-12 w-12 text-green-400 mb-2" />
                    <p className="font-semibold text-green-700">Currently In Stock</p>
                    <p className="text-sm text-gray-500 mt-1">This item has not been sold yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Lifecycle Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Lifecycle Timeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 flex-wrap">
                <TimelineStep
                  icon={<ShoppingCart className="h-4 w-4" />}
                  label="Purchased"
                  detail={result.purchase?.purchase_date ? formatDate(result.purchase.purchase_date) : ''}
                  subDetail={result.purchase?.supplier_name || ''}
                  active
                  color="bg-blue-500"
                />
                <ArrowRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                <TimelineStep
                  icon={<Package className="h-4 w-4" />}
                  label={isSold ? 'Was In Stock' : 'In Stock'}
                  detail={isSold ? '' : 'Currently Available'}
                  active
                  color="bg-green-500"
                />
                {isSold && (
                  <>
                    <ArrowRight className="h-5 w-5 text-gray-300 flex-shrink-0" />
                    <TimelineStep
                      icon={<FileText className="h-4 w-4" />}
                      label="Sold"
                      detail={result.sale?.bill_date ? formatDate(result.sale.bill_date) : ''}
                      subDetail={result.sale?.customer_name || ''}
                      active
                      color="bg-red-500"
                    />
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Placeholder when no search done */}
      {!result && !notFound && !loading && (
        <Card className="border-dashed border-2 border-gray-200">
          <CardContent className="pt-8 pb-8 text-center">
            <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-500">Enter a Serial Number to Track</p>
            <p className="text-sm text-gray-400 mt-1">View purchase history, current status, and sale details</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const InfoRow = ({ label, value, bold, mono }) => (
  <div className="flex justify-between items-center text-sm">
    <span className="text-gray-500">{label}</span>
    <span className={`text-gray-900 ${bold ? 'font-semibold' : ''} ${mono ? 'font-mono text-xs' : ''}`}>{value}</span>
  </div>
);

const TimelineStep = ({ icon, label, detail, subDetail, active, color }) => (
  <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${active ? 'bg-white shadow-sm' : 'bg-gray-50 opacity-50'}`}>
    <div className={`p-2 rounded-full text-white ${color}`}>
      {icon}
    </div>
    <div>
      <p className="font-semibold text-sm">{label}</p>
      {detail && <p className="text-xs text-gray-500">{detail}</p>}
      {subDetail && <p className="text-xs text-gray-400">{subDetail}</p>}
    </div>
  </div>
);

export default SerialNumberTracker;
